import { useQuery } from '@apollo/client/react';
import { Link } from 'react-router';
import { graphql } from '@/gql';
import type { TaxReportPeriodKind } from '@/gql/graphql';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { useSession } from '@/features/session/useSession';
import { formatCurrency, formatDateTime } from '@/lib/format';
import { hasPermission } from '@/lib/permissions';
import { useListHref } from '@/hooks/useListLocation';
import { useUrlState } from '@/hooks/useUrlState';
import { taxReportsUrlSchema } from './taxReportsUrlState';

const PAGE_SIZE = 50;

const TaxReportPeriodsDoc = graphql(/* GraphQL */ `
  query TaxReportPeriods($page: Int!, $pageSize: Int!) {
    taxReportPeriods(page: $page, pageSize: $pageSize) {
      nodes {
        kind
        recognitionDate
        year
        month
        label
        salesIncludingTax
        tips
        generatedAt
      }
      pageInfo {
        page
        pageSize
        totalRows
        totalPages
      }
    }
  }
`);

/**
 * Index of Omzetstaat periods: month totals and individual recognition days with sales.
 */
export function TaxReportsListPage() {
  const { me } = useSession();
  const canViewTax = hasPermission(me?.permissions, 'report:tax:read');
  const canViewDaily = canViewTax;
  const canViewMonthly = canViewTax;
  const { state: url, patch } = useUrlState(taxReportsUrlSchema);
  const listHref = useListHref();
  const page = url.page;
  const setPage = (next: number) => patch({ page: next });
  const { data, loading, error } = useQuery(TaxReportPeriodsDoc, {
    variables: { page, pageSize: PAGE_SIZE },
  });

  const connection = data?.taxReportPeriods;
  const periods = (connection?.nodes ?? []).filter((period) => {
    if (period.kind === 'MONTH') {
      return canViewMonthly;
    }

    return canViewDaily;
  });
  const pageInfo = connection?.pageInfo;

  if (loading && !connection) {
    return <div className="p-10 text-center text-sm text-gray-500">Loading…</div>;
  }
  if (error) {
    return <EmptyState title="Could not load tax reports" description={error.message} />;
  }

  const totalRows = pageInfo?.totalRows ?? 0;
  const totalPages = pageInfo?.totalPages ?? 1;
  const currentPage = pageInfo?.page ?? page;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Tax report</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Omzetstaat per recognition day or full calendar month.
          {totalRows > 0 && (
            <span className="ml-1">({totalRows.toLocaleString()} periods)</span>
          )}
        </p>
      </div>

      <Card
        title="Periods"
        actions={
          totalPages > 1 ? (
            <PaginationControls
              page={currentPage}
              totalPages={totalPages}
              onPageChange={setPage}
              disabled={loading}
            />
          ) : undefined
        }
      >
        {periods.length === 0 ? (
          <EmptyState
            title="No reports yet"
            description="No stored turnover reports with sales for this scope."
          />
        ) : (
          <>
            <div className="-mx-3 overflow-x-auto sm:mx-0">
              <table className="w-full min-w-[32rem] text-left text-sm">
                <thead className="text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2">Period</th>
                    <th className="px-3 py-2">Type</th>
                    <th className="px-3 py-2 text-right">Sales incl. tax</th>
                    <th className="px-3 py-2 text-right">Total tips</th>
                    <th className="px-3 py-2">Generated</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-white/10">
                  {periods.map((period) => (
                    <tr
                      key={periodRowKey(
                        period.kind,
                        period.recognitionDate,
                        period.year,
                        period.month,
                      )}
                      className={
                        period.kind === 'MONTH'
                          ? 'bg-gray-50/80 font-medium dark:bg-gray-800/50'
                          : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                      }
                    >
                      <td className="px-3 py-3">
                        <Link
                          state={{ from: listHref }}
                          to={periodLink(
                            period.kind,
                            period.recognitionDate,
                            period.year,
                            period.month,
                          )}
                          className="text-brand-600 hover:text-brand-700 dark:text-brand-400 dark:hover:text-brand-300"
                        >
                          {period.label}
                        </Link>
                      </td>
                      <td className="px-3 py-3">
                        <Badge tone={period.kind === 'MONTH' ? 'brand' : 'neutral'}>
                          {period.kind === 'MONTH' ? 'Month total' : 'Day'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {formatCurrency(period.salesIncludingTax)}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums">
                        {formatCurrency(period.tips)}
                      </td>
                      <td className="px-3 py-3 text-gray-500 dark:text-gray-400">
                        {period.generatedAt ? formatDateTime(period.generatedAt) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="mt-4 flex justify-end border-t border-gray-200 pt-4 dark:border-white/10">
                <PaginationControls
                  page={currentPage}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  disabled={loading}
                />
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function PaginationControls({
  page,
  totalPages,
  onPageChange,
  disabled,
}: {
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        Previous
      </Button>
      <span className="tabular-nums text-gray-600 dark:text-gray-300">
        Page {page} of {totalPages}
      </span>
      <Button
        variant="outline"
        size="sm"
        disabled={disabled || page >= totalPages}
        onClick={() => onPageChange(page + 1)}
      >
        Next
      </Button>
    </div>
  );
}

function periodRowKey(
  kind: TaxReportPeriodKind,
  recognitionDate: string | null | undefined,
  year: number | null | undefined,
  month: number | null | undefined,
): string {
  if (kind === 'MONTH') {
    return `month-${year}-${month}`;
  }
  return `day-${recognitionDate}`;
}

function periodLink(
  kind: TaxReportPeriodKind,
  recognitionDate: string | null | undefined,
  year: number | null | undefined,
  month: number | null | undefined,
): string {
  if (kind === 'MONTH' && year != null && month != null) {
    return `/tax-reports/month/${year}/${month}`;
  }
  return `/tax-reports/day/${recognitionDate ?? ''}`;
}
